import { colors } from "../../style";
import {
  apiPlaceholder,
  formatSpokenDuration,
} from "../../utils/meetingFormat";

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// "00:09:12" -> "9 min"; null when missing or unreadable
export const callLength = (hms) =>
  formatSpokenDuration(
    String(hms ?? "")
      .split(":")
      .reduce((t, n) => t * 60 + Number(n), 0),
  );

// The call's zone, only when it isn't the browser's
export const callZoneLabel = (timeZone) =>
  timeZone && timeZone !== LOCAL_TZ ? timeZone : null;

// Short zone name ("BST", "GMT-4") for a moment in `timeZone`
export function shortZoneName(iso, timeZone) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone,
      timeZoneName: "short",
    })
      .formatToParts(new Date(iso))
      .find((p) => p.type === "timeZoneName")?.value;
  } catch {
    return null;
  }
}

export const isLocalZone = (timeZone) => !timeZone || timeZone === LOCAL_TZ;

export const isOutgoing = (direction) => /^out/i.test(direction ?? "");
export const isIncoming = (direction) => /^in/i.test(direction ?? "");

const DIRECTION = {
  incoming: {
    label: "Incoming call",
    color: colors.teal,
    icon: "phoneIncoming",
  },
  outgoing: {
    label: "Outgoing call",
    color: colors.violet,
    icon: "phoneOutgoing",
  },
  unknown: { label: "Call", color: colors.call, icon: "phone" },
};

// Label, colour and icon for a call's direction
export const directionFor = (direction) =>
  isOutgoing(direction)
    ? DIRECTION.outgoing
    : isIncoming(direction)
      ? DIRECTION.incoming
      : DIRECTION.unknown;

// The call in the shape the shared meeting pieces read
export function callAsMeeting(call) {
  const summary =
    call.has_summary && !apiPlaceholder(call.summary)
      ? (call.summary ?? "").trim()
      : "";
  const blocks = call.transcription?.conversation ?? [];
  // While transcribing, or with nothing to show, the API sends one bracketed
  // stand-in block instead of turns
  const stand =
    blocks.length === 1 && apiPlaceholder(blocks[0].content)
      ? apiPlaceholder(blocks[0].content)
      : null;
  return {
    sqid: call.sqid,
    status: call.status,
    title: callTitle(call),
    recorded_at: call.start_utc,
    has_transcription: !!call.has_transcription,
    summary,
    meeting_notes: call.meeting_notes ?? [],
    meeting_notes_template: call.meeting_notes_template,
    sentiment: call.sentiment
      ? {
          rating: call.sentiment.rating,
          summary: apiPlaceholder(call.sentiment.summary)
            ? null
            : call.sentiment.summary,
        }
      : null,
    // Drops the API's speaker colours for the meeting palette: it sends
    // rgba(0,0,0,0) for one speaker and a dark violet that's hard to read
    transcription: {
      conversation: stand
        ? []
        : blocks.map((b) => ({ ...b, color: undefined })),
    },
    transcriptPlaceholder: stand,
  };
}

export const callTitle = (call) =>
  call.user_name || call.tel_number || "Withheld";
